import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../../../api';
import { useCookies } from '../../../../hooks/useCookies';

const UserProfileContext = createContext();

export const useUserProfile = () => {
    const context = useContext(UserProfileContext);
    if (!context) {
        throw new Error('useUserProfile must be used within a UserProfileProvider');
    }
    return context;
};

export default function UserProfileProvider({ children }) {
    const [profiles, setProfiles] = useState([]);
    const cookies = useCookies();
    const [wave, setWave] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [refresh, setRefresh] = useState(1);

    useEffect(() => {
        if (!cookies.getCookie('token')) return;

        const fetchProfiles = async (currentWave) => {
            setLoading(true);

            try {
                const response = await fetch(`${API_URL}customer/user/profiles?page=${page}&wave=${currentWave}`, {
                    method: "GET",
                    headers: {
                        'Authorization': `Bearer ${cookies.getCookie('token')}`,
                    }
                });
                const data = await response.json();
                if (!data) return;

                if (response.ok) {
                    if (data?.results?.length === 0 && currentWave < 2) {
                        setWave((prevWave) => {
                            const newWave = parseInt(prevWave) + 1;
                            return newWave;
                        });
                    } else {
                        setProfiles(data?.results);
                        if (data.currentPage) {
                            setPage(data.currentPage);
                        }
                    }
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                } else {
                    console.error('UserProfileProvider: Fetch error:', error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfiles(wave);
    }, [refresh, page, wave]);

    const value = useMemo(() => ({
        profiles,
        setProfiles,
        loading,
        setLoading,
        page,
        setPage,
        wave,
        setWave,
        refresh,
        setRefresh
    }), [
        profiles,
        loading,
        page,
        wave,
        refresh
    ]);

    return (
        <UserProfileContext.Provider value={value}>
            {children}
        </UserProfileContext.Provider>
    );
}
